import cloneDeep from 'lodash/cloneDeep';
import type {
  DataRoute,
  Expression,
  ExpressionTerm,
  PendingParenthesisRelativeLocation,
} from 'flow/store/engine/types';
import { getParentPathAndExpressionItem } from '../../../store/redux/helpers';
import { getExpressionFunction } from '../../../store/engine/utils';
import { CaretPositionSuffixTypes } from '../NodeEditorDrawer/types';
import { getGroupStartIndexOffset } from '../useCaretControl/helpers';

export function flattenExpressionIndices({
  expressionToFlatten,
  prefixIndex = undefined,
  dataRoutes,
  pendingGroupPath,
  pendingParanthesisSide = 'after',
}: {
  expressionToFlatten: Expression;
  prefixIndex?: string;
  dataRoutes: Record<string, DataRoute>;
  pendingGroupPath?: string;
  pendingParanthesisSide?: PendingParenthesisRelativeLocation;
}) {
  // a top level min/max is not inside a group, so there is nowhere to draw "Min ("
  const groupFunction = prefixIndex === undefined
    ? undefined
    : getExpressionFunction(expressionToFlatten);

  return expressionToFlatten.reduce(
    (
      result: ExpressionTerm[],
      expressionItem: ExpressionTerm | ExpressionTerm[],
      expressionItemIndex: number,
    ) => {
      const expressionIndex = prefixIndex !== undefined
        ? `${prefixIndex}-${expressionItemIndex}`
        : String(expressionItemIndex);
      const isPendingGroupPath = String(expressionIndex) === pendingGroupPath;

      if (Array.isArray(expressionItem)) {
        // expressionItem is a group
        const nestedIndices = flattenExpressionIndices({
          expressionToFlatten: expressionItem,
          prefixIndex: expressionIndex,
          dataRoutes,
          pendingGroupPath,
          pendingParanthesisSide,
        });

        const newExpression = [...result];

        if (isPendingGroupPath && pendingParanthesisSide === 'before') {
          newExpression.push(`${expressionIndex}${CaretPositionSuffixTypes.GROUP_START_PENDING}`);
        }

        newExpression.push(`${expressionIndex}${CaretPositionSuffixTypes.GROUP_START}`);

        if (isPendingGroupPath && pendingParanthesisSide === 'after') {
          newExpression.push(`${expressionIndex}${CaretPositionSuffixTypes.GROUP_START_PENDING}`);
        }

        if (Array.isArray(nestedIndices)) newExpression.push(...nestedIndices);
        else newExpression.push(nestedIndices);

        newExpression.push(`${expressionIndex}${CaretPositionSuffixTypes.GROUP_END}`);

        return newExpression;
      }

      // min/max operators are drawn as plain commas, so they get no index and the caret skips them
      if (groupFunction && expressionItem === groupFunction) return result;

      const newExpression = [...result];

      if (isPendingGroupPath && pendingParanthesisSide === 'before') {
        newExpression.push(`${expressionIndex}${CaretPositionSuffixTypes.GROUP_START_PENDING}`);
      }

      if (typeof expressionItem === 'object' && expressionItem.data_route_id) {
        newExpression.push(`${expressionIndex}-0${CaretPositionSuffixTypes.DATAROUTE}`);

        const dataRoute = dataRoutes[expressionItem.data_route_id];
        if (dataRoute.attribute?.id) {
          newExpression.push(`${expressionIndex}-1${CaretPositionSuffixTypes.DATAROUTE}`);
        }
      } else {
        newExpression.push(String(expressionIndex));
      }

      if (isPendingGroupPath && pendingParanthesisSide === 'after') {
        newExpression.push(`${expressionIndex}${CaretPositionSuffixTypes.GROUP_START_PENDING}`);
      }

      return newExpression;
    }, []) as string[];
}

export function getPossibleGroupClosingPathIndex({
  expressions,
  pendingGroupPathIndices,
  pendingGroupSide = 'after',
}: {
  expressions: Expression;
  pendingGroupPathIndices: string[];
  pendingGroupSide: 'after' | 'before';
}): {
    parentOfGroupOpeningPath: string;
    itemsWithinMaxRange: (ExpressionTerm | ExpressionTerm[])[];
  } {
  const originalExpression = cloneDeep(expressions);
  const lastOpeningIndex = Number(pendingGroupPathIndices.at(-1));

  const isFirstDropdown = pendingGroupSide === 'before'
  && pendingGroupPathIndices.length === 1
  && lastOpeningIndex === 0;

  if (isFirstDropdown) {
    return {
      parentOfGroupOpeningPath: '',
      itemsWithinMaxRange: expressions,
    };
  }

  // When we set a paranthesis to be before an expression item,
  // we want to ensure that we don't remove the expression item it precedes
  const firstElementRemovalIndexOffset = getGroupStartIndexOffset(pendingGroupSide);
  if (pendingGroupPathIndices.length === 1) {
    // opening at the top level

    const maxRangeInTheExpressionFromOpeningPath = originalExpression.splice(
      lastOpeningIndex + firstElementRemovalIndexOffset);

    return {
      parentOfGroupOpeningPath: '',
      itemsWithinMaxRange: maxRangeInTheExpressionFromOpeningPath,
    };
  }

  const {
    pathWithoutLastIndex,
    parentExpressionArrayOfTheExpressionOnTheLastIndex,
  } = getParentPathAndExpressionItem({
    indices: pendingGroupPathIndices,
    existingExpression: originalExpression,
  });
  // remove and retrieve "cut" the items in the range of group opening path / closing path
  const maxRangeInTheExpressionFromOpeningPath = parentExpressionArrayOfTheExpressionOnTheLastIndex
    .splice(lastOpeningIndex + firstElementRemovalIndexOffset);

  return {
    parentOfGroupOpeningPath: pathWithoutLastIndex,
    itemsWithinMaxRange: maxRangeInTheExpressionFromOpeningPath,
  };
}
